import { Target } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface DealPillProps {
  deal: {
    name: string;
    amount: string | number | null;
    stage: string | null;
    hubspotCompany: { name: string } | null;
  };
  variant?: 'compact' | 'full';
  className?: string;
  /**
   * La etapa ya resuelta a su nombre (o `null` para no mostrar ninguna). Sin esto se muestra `deal.stage` tal cual,
   * como siempre.
   */
  etapa?: string | null;
}

function formatAmount(amount: string | number | null): string {
  if (amount === null) return '—';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

export function DealPill({ deal, variant = 'compact', className, etapa }: DealPillProps) {
  const company = deal.hubspotCompany?.name ?? 'Sin empresa';
  if (variant === 'compact') {
    return (
      <div className={cn('inline-flex items-center gap-1.5 text-xs', className)}>
        <Target className="h-3 w-3 text-info" />
        <span className="font-medium truncate max-w-[140px]" title={`${company} · ${deal.name}`}>
          {company}
        </span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">{formatAmount(deal.amount)}</span>
      </div>
    );
  }
  return (
    <div
      className={cn('flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2', className)}
    >
      <Target className="h-4 w-4 text-info" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{deal.name}</div>
        <div className="text-xs text-muted-foreground truncate">{company}</div>
      </div>
      <div className="text-right">
        <div className="font-semibold">{formatAmount(deal.amount)}</div>
        {(etapa === undefined ? deal.stage : etapa) && (
          <Badge variant="outline" className="text-xs">
            {etapa === undefined ? deal.stage : etapa}
          </Badge>
        )}
      </div>
    </div>
  );
}
